import { useCallback, useEffect, useMemo, useState } from 'react';

type UseExpandedFolderIdsParams = {
  organizationId: string;
  projectId?: string;
  /** Folders a search opened; these stay open even if the user collapsed them. */
  autoExpandedFolderIds: Set<string>;
};

/** Tracks which document folders are expanded inline. Collapses when scope changes. */
export function useExpandedFolderIds({
  organizationId,
  projectId,
  autoExpandedFolderIds,
}: UseExpandedFolderIdsParams) {
  const [userExpandedFolderIds, setUserExpandedFolderIds] = useState<
    Set<string>
  >(() => new Set());

  const expandedFolderIds = useMemo(
    () => new Set([...userExpandedFolderIds, ...autoExpandedFolderIds]),
    [autoExpandedFolderIds, userExpandedFolderIds],
  );

  const toggleFolder = useCallback(
    (folderId: string) => {
      const isExpanded = expandedFolderIds.has(folderId);
      setUserExpandedFolderIds((prev) => {
        const next = new Set(prev);
        if (isExpanded) next.delete(folderId);
        else next.add(folderId);
        return next;
      });
    },
    [expandedFolderIds],
  );

  useEffect(() => {
    setUserExpandedFolderIds(new Set());
  }, [organizationId, projectId]);

  return { expandedFolderIds, toggleFolder };
}
